import { DateInput } from "../components/DateInput";
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AlertTriangle } from "lucide-react";

import { tr, lbl } from "../lib/i18n";
const MODULES = ["", "auth", "vehicles", "users", "settings"];
const ACTIONS = ["", "login", "logout", "create", "update", "delete", "status_change", "cost_add", "cost_delete", "change_password"];

export default function ActivityLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: "", module: "", action: "", date_from: "", date_to: "" });
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setError(null);
      try {
        const params = Object.fromEntries(Object.entries({ ...filters, page, limit: 50 }).filter(([, v]) => v !== "" && v != null));
        const { data } = await api.get("/audit", { params });
        if (!alive) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (alive) { setError(formatApiError(e)); setItems([]); setTotal(0); }
      }
    })();
    return () => { alive = false; };
  }, [filters, page]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-5" data-testid="activity-log-page">
      <div>
        <div className="eyebrow">{tr("x.compliance")}</div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("x.activity_log")}</h1>
        <p className="text-sm text-[var(--gray-400)] mt-1">{tr("x.every_create_update_and_delete_across_th")}</p>
      </div>

      {error && (
        <div className="panel p-4 flex items-start gap-3 border-l-4 border-red-500" data-testid="activity-error">
          <AlertTriangle size={18} className="text-red-500 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">{tr("x.could_not_load_activity_log")}</div>
            <div className="text-xs text-[var(--gray-400)] mt-0.5">{error}</div>
          </div>
        </div>
      )}

      <div className="panel p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
        <select value={filters.user_id} onChange={(e) => { setFilters({ ...filters, user_id: e.target.value }); setPage(1); }} className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm">
          <option value="">{tr("x.any_user")}</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <select value={filters.module} onChange={(e) => { setFilters({ ...filters, module: e.target.value }); setPage(1); }} className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm">
          {MODULES.map((m) => <option key={m} value={m}>{m ? lbl(m) : tr("x.any_module")}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(1); }} className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm">
          {ACTIONS.map((a) => <option key={a} value={a}>{a ? lbl(a) : tr("x.any_action")}</option>)}
        </select>
        <DateInput type="date" value={filters.date_from} onChange={(e) => { setFilters({ ...filters, date_from: e.target.value }); setPage(1); }} className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm" />
        <DateInput type="date" value={filters.date_to} onChange={(e) => { setFilters({ ...filters, date_to: e.target.value }); setPage(1); }} className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-sm" />
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>{tr("x.when")}</th>
              <th>{tr("x.actor")}</th>
              <th>{tr("x.module")}</th>
              <th>{tr("x.action")}</th>
              <th>{tr("x.entity")}</th>
              <th>{tr("x.changes")}</th>
              <th>{tr("x.ip")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td className="mono text-xs">{it.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="font-medium">{it.actor_name}</td>
                <td><span className="pill pill-prep">{lbl(it.module)}</span></td>
                <td>{lbl(it.action)}</td>
                <td className="text-xs">{lbl(it.entity_label) || "—"}</td>
                <td className="text-xs">
                  {it.diff?.length ? (
                    <ul className="space-y-0.5">
                      {it.diff.slice(0, 3).map((d, i) => (
                        <li key={i}><span className="text-[var(--gray-400)]">{d.field}:</span> {lbl(String(d.before ?? "—"))} → <span className="font-semibold">{lbl(String(d.after ?? "—"))}</span></li>
                      ))}
                      {it.diff.length > 3 && <li className="text-[var(--gray-400)]">+{it.diff.length - 3} {tr("x.more")}</li>}
                    </ul>
                  ) : "—"}
                </td>
                <td className="mono text-xs">{it.ip_address || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[var(--gray-400)]">{tr("x.no_activity_yet")}</td></tr>}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm">
          <div className="text-[var(--gray-400)]">{tr("x.page")} {page} {tr("x.of")} {totalPages}</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40">{tr("x.previous")}</button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40">{tr("x.next")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
